/**
 * MerchantTravelModule — the menu path to the Merchant Hive (F9 / the "Hive"
 * toolbar button) and the way back.
 *
 * F9 outside the hive: `travelToMerchantHive` saves EXACTLY where you stand
 * (world + position + yaw, one row per identity) and drops you at the hive's
 * arrival plaza. F9 inside the hive — or the floating "Return" chip that
 * appears whenever a saved return point exists — calls `returnFromTravel`,
 * which puts you back where you were, whenever you like. The server moves
 * playerState; the client ADOPTS the move (`atlas:world-adopted`, the space
 * planetfall pattern) — it never re-enters via the enterWorld reducer, which
 * would stomp the saved-return semantics.
 */
import type { App, IModule } from 'enginex';
import { atlas } from '../state.js';
import { bus } from '../bus.js';

interface ReturnRow { identity: { toHexString(): string }; worldId: bigint; posX: number; posZ: number }

export class MerchantTravelModule implements IModule {
  readonly name = 'MerchantTravel';
  private chip: HTMLButtonElement | null = null;
  private busy = false;

  init(app: App): void {
    void app;
    window.addEventListener('keydown', (e) => {
      if (e.code !== 'F9' || e.ctrlKey || e.metaKey) return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA')) return;
      e.preventDefault();
      this.toggleTravel();
    });
    this.buildChip();
    // Dev hook (probes): the saturated-tab law starves in-page reducer sends,
    // so harnesses travel out-of-band (node helper) and ADOPT here.
    (window as unknown as { __hiveTravel?: unknown }).__hiveTravel = {
      travel: (): void => this.toggleTravel(),
      adopt: (wid: string | number | bigint, x: number, z: number): void =>
        bus.emit('atlas:world-adopted', { worldId: BigInt(wid), x, z }),
      state: (): { busy: boolean; hive: boolean } => ({ busy: this.busy, hive: this.hive() !== null }),
      refresh: (): void => this.refreshChip(),
    };
    // travel_return rides the phase-1 base subscription (AtlasNetworkModule)
    // — a phase-2 subscribe would ride the saturated post-entry socket. Here
    // we only hook the row callbacks that keep the chip live.
    bus.on('atlas:account-ready', () => {
      const conn = atlas.conn;
      try {
        const tbl = (conn?.db as unknown as { travelReturn?: { onInsert(f: () => void): void; onDelete(f: () => void): void } }).travelReturn;
        tbl?.onInsert(() => this.refreshChip());
        tbl?.onDelete(() => this.refreshChip());
        this.refreshChip();
      } catch { /* pre-publish server */ }
    });
    bus.on('atlas:world-entered', () => this.refreshChip());
  }

  /** A starved socket can leave a reducer promise unsettled FOREVER — without
   *  this watchdog `busy` latched true and F9 went permanently dead. */
  private armBusy(): void {
    this.busy = true;
    const gen = ++this.busyGen;
    window.setTimeout(() => { if (gen === this.busyGen) this.busy = false; }, 12_000); // an older watchdog must not unlock a newer travel
  }
  private busyGen = 0;

  private hive(): { worldId: bigint } | null {
    const conn = atlas.conn;
    if (!conn) return null;
    for (const w of conn.db.world.iter()) if (w.kind === 'merchant') return w;
    return null;
  }

  private myReturn(): ReturnRow | null {
    const conn = atlas.conn;
    const me = atlas.identityHex;
    if (!conn || me === null) return null;
    const tbl = (conn.db as unknown as { travelReturn?: { iter(): Iterable<ReturnRow> } }).travelReturn;
    try {
      for (const r of tbl?.iter() ?? []) if (r.identity.toHexString() === me) return r;
    } catch { /* pre-publish */ }
    return null;
  }

  private toggleTravel(): void {
    const hive = this.hive();
    if (!hive || atlas.currentWorldId === null || this.busy) return;
    if (atlas.currentWorldId === hive.worldId) this.doReturn();
    else this.doTravel(hive.worldId);
  }

  private doTravel(hiveId: bigint): void {
    const conn = atlas.conn;
    if (!conn) return;
    this.armBusy();
    const r = conn.reducers as unknown as { travelToMerchantHive(a: Record<string, never>): Promise<void> };
    void r.travelToMerchantHive({})
      .then(() => {
        bus.emit('atlas:world-adopted', { worldId: hiveId, x: 0, z: 0 });
        bus.emit('atlas:status', { text: 'Merchant Hive — F9 (or the Return chip) puts you back where you were.' });
      })
      .catch((e: Error) => bus.emit('atlas:status', { text: `Hive travel refused: ${e.message}` }))
      .finally(() => { this.busy = false; this.busyGen++; this.refreshChip(); });
  }

  private doReturn(): void {
    const conn = atlas.conn;
    if (!conn) return;
    const saved = this.myReturn();
    let dest: { worldId: bigint; x: number; z: number } | null = saved
      ? { worldId: saved.worldId, x: saved.posX, z: saved.posZ } : null;
    if (dest && ![...conn.db.world.iter()].some((w) => w.worldId === dest!.worldId)) dest = null;
    if (!dest) {
      // Mirror the server's fallback: a vanished saved world returns to the Commons spawn.
      for (const w of conn.db.world.iter()) if (w.kind === 'commons') { dest = { worldId: w.worldId, x: 0, z: 0 }; break; }
    }
    if (!dest) return;
    this.armBusy();
    const r = conn.reducers as unknown as { returnFromTravel(a: Record<string, never>): Promise<void> };
    const target = dest;
    void r.returnFromTravel({})
      .then(() => bus.emit('atlas:world-adopted', { worldId: target.worldId, x: target.x, z: target.z }))
      .catch((e: Error) => bus.emit('atlas:status', { text: `Return refused: ${e.message}` }))
      .finally(() => { this.busy = false; this.busyGen++; this.refreshChip(); });
  }

  // ── The Return chip — visible whenever a saved return point exists ─────────
  private buildChip(): void {
    const chip = document.createElement('button');
    chip.id = 'travel-return';
    chip.textContent = '↩ Return';
    chip.title = 'Teleport back to where you were before the Merchant Hive (F9)';
    chip.style.cssText = 'position:fixed;right:14px;bottom:64px;z-index:42;display:none;'
      + 'padding:7px 12px;border-radius:8px;background:rgba(20,38,30,0.85);color:#bff0d0;'
      + 'border:1px solid #2c7a4f;font:600 12px system-ui;cursor:pointer';
    chip.onclick = () => this.doReturn();
    document.body.appendChild(chip);
    this.chip = chip;
  }

  private refreshChip(): void {
    if (!this.chip) return;
    this.chip.style.display = this.myReturn() !== null ? 'block' : 'none';
  }

  dispose(): void {
    this.chip?.remove();
  }
}
